import { defaultPathComponent } from '@common/constants/shared';
import {
  formValueSelector,
  getDefaultValueFields,
} from '@common/redux/selectors/formInputs';
import {
  setDefaultValueInput,
  setFormValue,
  setValueInput,
} from '@common/redux/slice/formInputs';
import { ComponentType } from '@common/types/element';
import {
  convertImage,
  getValueBindingComponent,
  memoriesConditional,
} from '@common/utils/component';
import { get, isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const connectorInput = (InputComponent: any) =>
  React.memo((props: any) => {
    const reduxFormFields = useSelector(formValueSelector);
    const defaultValues = useSelector(getDefaultValueFields);

    const { dependencies = {} } = props;
    const { valueInputs } = dependencies;
    const dispatch = useDispatch();
    const ref = useRef(0);

    const changeInput = useCallback(
      (value: any) => {
        const valueInput = {
          [props.id]: value,
        };

        if (props?.isFormItem) {
          dispatch(setFormValue(valueInput));
        } else {
          dispatch(setValueInput(valueInput));
        }
      },
      [props.id, dispatch]
    );

    const setDefaultValue = (value: string) => {
      const valueInput = {
        [props.id]: value,
      };
      dispatch(setDefaultValueInput(valueInput));
    };

    useEffect(() => {
      ref.current += 1;
    });

    const getDefaultValue = useCallback(() => {
      const valueBinding = getValueBindingComponent(
        props,
        defaultPathComponent[props.type]
      );
      switch (props.type) {
        case ComponentType.datePicker:
          const date = moment().second(0).millisecond(0).toISOString(); // round second to 0 to avoid re-render
          const isNotBinding =
            props?.defaultDateValue === 'currentTime' ||
            props?.defaultDateValue?.label === 'Current Time';

          return isNotBinding ? date : valueBinding;

        case ComponentType.imageUpload:
          const image = convertImage(props);
          if (!isEmpty(image)) {
            return typeof image === 'string' ? { url: image } : image;
          } else {
            return valueBinding && typeof valueBinding === 'string'
              ? { url: valueBinding }
              : valueBinding;
          }

        case ComponentType.select:
          if (JSON.stringify(props?.defaultValue || null).includes(props?.id))
            return;
          return get(props, 'dataBinding[0].defaultValue');

        default:
          return valueBinding;
      }
    }, [
      props.id,
      props.dataBinding,
      props.data?.defaultValue,
      props.data?.defaultDateValue,
    ]);

    const valueInput = useMemo(() => {
      if (props?.isFormItem) {
        return get(reduxFormFields, props.id);
      }
      return get(valueInputs, props.id);
    }, [valueInputs, props.id, reduxFormFields]);

    // this hook only run once when databinding is fetched
    useEffect(() => {
      const isDatePickerRender =
        props.type === ComponentType.datePicker && ref.current >= 5; // ignore re-render for datePicker. Must be >=5 to get data from databinding.
      if (props?.isFormItem || isDatePickerRender) {
        return;
      }

      const defaultValue = getDefaultValue();

      if (
        (defaultValue || defaultValues[props.id]) &&
        !isEqual(defaultValue, defaultValues[props.id])
      ) {
        if (props.type === ComponentType.datePicker) {
          setDefaultValue(defaultValue || defaultValues[props.id]);
        } else {
          setDefaultValue(defaultValue);
        }
      }
    }, [getDefaultValue, defaultValues[props.id]]);

    const placeholder = get(
      props,
      'defaultValue.binding.options',
      props?.placeholder
    );

    const params = {
      changeInput,
      setDefaultValue,
      valueInput,
      placeholder,
    };

    return <InputComponent {...props} {...params} />;
  }, memoriesConditional(['dependencies', 'data', 'id', 'dataBinding']));

export default connectorInput;
